import { Log, WebStorageStateStore } from "oidc-client-ts";
import type { StateStore, UserManagerSettings } from "oidc-client-ts";

/**
 * Prefix applied to all oidc-client-ts web storage keys.
 *
 * Example OIDC user key becomes:
 *   `onecap.oidc.user:<authority>:<clientId>`
 */
export const OIDC_STORAGE_PREFIX = "onecap.";


export type OidcEnv = Readonly<{
  authority: string;
  clientId: string;
  redirectUri: string;
  postLogoutRedirectUri: string;
  scope: string;
}>;

export type OidcRuntime = Readonly<{
  localStorage: Storage;
  sessionStorage: Storage;
}>;

export type LoginState = Readonly<{
  returnTo: string;
}>;

export function readOidcEnv(): OidcEnv {
  /**
   * IMPORTANT (Next.js): these must be read via **static property access**
   * (e.g. `process.env.NEXT_PUBLIC_OIDC_AUTHORITY`) so the values can be
   * inlined into the client bundle. Dynamic access like `process.env[key]`
   * will appear `undefined` in the browser.
   */
  const authority = process.env.NEXT_PUBLIC_OIDC_AUTHORITY;
  const clientId = process.env.NEXT_PUBLIC_OIDC_CLIENT_ID;
  const redirectUri = process.env.NEXT_PUBLIC_OIDC_REDIRECT_URI;
  const postLogoutRedirectUri = process.env.NEXT_PUBLIC_OIDC_POST_LOGOUT_REDIRECT_URI;
  const scope = process.env.NEXT_PUBLIC_OIDC_SCOPE;

  const missing: string[] = [];
  if (!authority || authority.trim() === "") missing.push("NEXT_PUBLIC_OIDC_AUTHORITY");
  if (!clientId || clientId.trim() === "") missing.push("NEXT_PUBLIC_OIDC_CLIENT_ID");
  if (!redirectUri || redirectUri.trim() === "") missing.push("NEXT_PUBLIC_OIDC_REDIRECT_URI");
  if (!postLogoutRedirectUri || postLogoutRedirectUri.trim() === "")
    missing.push("NEXT_PUBLIC_OIDC_POST_LOGOUT_REDIRECT_URI");
  if (!scope || scope.trim() === "") missing.push("NEXT_PUBLIC_OIDC_SCOPE");
  if (missing.length > 0) {
    throw new Error(`Missing required OIDC env vars: ${missing.join(", ")}`);
  }

  const resolvedAuthority = String(authority);
  const resolvedClientId = String(clientId);
  const resolvedRedirectUri = String(redirectUri);
  const resolvedPostLogoutRedirectUri = String(postLogoutRedirectUri);
  const resolvedScope = String(scope);

  // Basic validation to fail fast in production.
  // Security: ensure we don't accidentally construct open-redirects via malformed URIs.
  // (Cognito Hosted UI also enforces allowed callback/logout URLs, but we validate anyway.)
  // eslint-disable-next-line no-new
  new URL(resolvedAuthority);
  // eslint-disable-next-line no-new
  new URL(resolvedRedirectUri);
  // eslint-disable-next-line no-new
  new URL(resolvedPostLogoutRedirectUri);

  return {
    authority: resolvedAuthority,
    clientId: resolvedClientId,
    redirectUri: resolvedRedirectUri,
    postLogoutRedirectUri: resolvedPostLogoutRedirectUri,
    scope: resolvedScope,
  };
}

export function buildOidcSettings(
  oidcEnv: OidcEnv,
  runtime: OidcRuntime
): UserManagerSettings {
  // Security: do not enable OIDC library debug logs in production.
  // Also: avoid console logging except for error logging elsewhere.
  Log.setLogger({
    // oidc-client-ts expects these methods; we keep them no-op.
    debug: () => undefined,
    info: () => undefined,
    warn: () => undefined,
    error: (..._args: unknown[]) => undefined,
  });
  Log.setLevel(Log.NONE);

  const userStore = new WebStorageStateStore({ store: runtime.localStorage });
  const stateStore = new WebStorageStateStore({ store: runtime.sessionStorage });

  return {
    authority: oidcEnv.authority,
    client_id: oidcEnv.clientId,
    redirect_uri: oidcEnv.redirectUri,
    // For SPAs, silent renew is either done via refresh tokens or an iframe to `silent_redirect_uri`.
    // We use the same route and detect iframe vs top-level in `pages/callback.tsx`.
    silent_redirect_uri: oidcEnv.redirectUri,
    post_logout_redirect_uri: oidcEnv.postLogoutRedirectUri,
    response_type: "code", // Auth Code + PKCE (PKCE handled internally by oidc-client-ts)
    scope: oidcEnv.scope,

    // Token persistence requirement: store the user (tokens) in localStorage explicitly.
    userStore,
    // Non-token transient state should still be stored in web storage (sessionStorage) not memory.
    stateStore,

    // Security: the backend must validate the access token; do NOT send the ID token to APIs.
    // (We enforce this in the Axios interceptor and expose `accessToken` separately.)
    // Fetch additional claims from the OIDC UserInfo endpoint and merge into `user.profile`.
    // This is needed because the ID token will only contain a subset of attributes based on scopes
    // and provider token configuration (Cognito often omits many profile attributes otherwise).
    loadUserInfo: true,

    // Renew behavior.
    automaticSilentRenew: false,
    // Refresh token rotation is used by oidc-client-ts automatically when a refresh token is present;
    // otherwise it falls back to iframe-based silent renew.

    // Clock skew handling: renew early to reduce risk of the access token expiring mid-request.
    // (oidc-client-ts v3.x does not expose a `clockSkew` setting.)
    accessTokenExpiringNotificationTimeInSeconds: 120,

    // Keep stale cleanup enabled.
    staleStateAgeInSeconds: 300,

    // Reduce noisy session monitoring for providers that don't support check_session_iframe well.
    monitorSession: false,

    // Server-side token revocation on signout (safety net for library-managed signout flows).
    // Cognito's /oauth2/revoke only accepts refresh tokens; revoking the refresh token also
    // invalidates all access tokens issued from it.
    revokeTokensOnSignout: true,
    revokeTokenTypes: ["refresh_token"],
  };
}

